import React, { FunctionComponent } from "react";

import AlertCircleIcon from "@uui/AlertCircle";

import { SvgIconProps, Typography, TypographyProps } from "@mui/material";
import styled from "@mui/material/styles/styled";

import type { PartialBy } from "@app/common/types";

import IconBackground, { IconBackgroundProps } from "@rui/IconBackground";
import Link from "@rui/Link";
import { Column, StackProps } from "@rui/Stack";

type EmptyStateContainerProps = StackProps & {
  border?: React.CSSProperties["border"];
  backgroundColor?: React.CSSProperties["backgroundColor"];
};

export const EmptyStateOuterContainer = styled(
  (props: EmptyStateContainerProps) => <Column variant="center" {...props} />
)(({ backgroundColor, border, theme }) => ({
  backgroundColor: backgroundColor ?? "#FAFAFA",
  borderRadius: "8px",
  padding: theme.spacing(2),
  border: border ?? `2px dashed ${theme.palette.shades.neutral[100]}`,
  flexGrow: 1,
  textAlign: "center",
  color: theme.palette.shades.neutral[600],
}));

const EmptyStateInnerContainer = styled((props: StackProps) => (
  <Column variant="center" spacing={1} {...props} />
))({ maxWidth: "400px" });

export const EmptyStateTitle = styled((props: TypographyProps) => (
  <Typography variant="bodyPrimaryMed" {...props} />
))({});

export const EmptyStateSubtext = styled((props: TypographyProps) => (
  <Typography variant="bodySecondaryReg" {...props} />
))({ paddingBottom: "8px" });

export const EmptyStateSubtextLink = styled(Link)(({ theme }) => ({
  color: theme.palette.shades.primary[500],
  fontWeight: 500,
}));

type EmptyStateIconType = "primary" | "error";
type EmptyStateIconProps = Partial<IconBackgroundProps> & {
  Icon: FunctionComponent<SvgIconProps>;
  type?: EmptyStateIconType;
};
type EmptyStateErrorIconProps = PartialBy<EmptyStateIconProps, "Icon">;

const COLORS_BY_TYPE: {
  [key in EmptyStateIconType]: {
    bgcolor: React.CSSProperties["backgroundColor"];
    color: React.CSSProperties["color"];
  };
} = {
  primary: {
    bgcolor: "shades.primary.50",
    color: "shades.primary.500",
  },
  error: {
    bgcolor: "shades.negative.50",
    color: "shades.negative.600",
  },
};

export const EmptyStateIcon = ({
  Icon,
  height = 32,
  type = "primary",
  ...iconBackgroundProps
}: EmptyStateIconProps) => {
  const { bgcolor, color } = COLORS_BY_TYPE[type];
  return (
    <IconBackground {...iconBackgroundProps} height={height} sx={{ bgcolor }}>
      <Icon sx={{ color }} />
    </IconBackground>
  );
};

export const EmptyStateErrorIcon = ({
  Icon = AlertCircleIcon,
  ...props
}: EmptyStateErrorIconProps) => (
  <EmptyStateIcon Icon={Icon} {...props} type="error" />
);

export const EmptyStateContainer = ({
  backgroundColor,
  border,
  ...props
}: EmptyStateContainerProps) => (
  <EmptyStateOuterContainer backgroundColor={backgroundColor} border={border}>
    <EmptyStateInnerContainer {...props} />
  </EmptyStateOuterContainer>
);
